import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaNetworkWired,
  FaCoffee,
  FaBuilding,
  FaStickyNote,
  FaPencilAlt,
  FaTrash,
  FaKey,
  FaEye,
  FaEyeSlash,
  FaCopy,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaSave,
  FaUndo,
  FaToggleOn,
  FaToggleOff,
  FaSyncAlt,
} from "react-icons/fa";
import useUpdateDeviceData from "../../../Hooks/device/useUpdateDeviceData";
import useUpdateDeviceSyncData from "../../../Hooks/device/useUpdateDeviceSyncData";
import {
  useGetPaymentRejectionsQuery,
  useRemoveDeviceProductMutation,
  useUpdateDeviceInformationMutation,
} from "../../../store/storageRoles/devicesSharedApi";
import {
  useDealerUpdateDeviceInfoMutation,
} from "../../../store/dealerRoles/devicesApi";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { AppLink } from "../../shared";
import DeviceVersionsCard from "./DeviceVersionsCard";
import EmployeeOnlyPaymentCard from "./EmployeeOnlyPaymentCard";
import StorageManualQuotaRequestSection from "./storageRoles/ManualQuotaRequestSection";
import { getFirmTypeStyle } from "../../../utils/firmTypeStyles";

// ─── Uyarı kotası / senk seviyesi sınırları (ProductEditForm.js ile aynı) ───
const QUOTA_WARNING_MIN = 100;
const QUOTA_WARNING_MAX = 5000;
const SYNC_LEVEL_MIN = 10;
const SYNC_LEVEL_MAX = 1000;

const getProductFieldErrors = (edit) => {
  const errors = {};
  if (edit?.quotaWarning < QUOTA_WARNING_MIN) errors.quotaWarning = `Minimum ${QUOTA_WARNING_MIN} olmalıdır`;
  else if (edit?.quotaWarning > QUOTA_WARNING_MAX) errors.quotaWarning = `Maksimum ${QUOTA_WARNING_MAX} olmalıdır`;
  if (edit?.syncLevel < SYNC_LEVEL_MIN) errors.syncLevel = `Minimum ${SYNC_LEVEL_MIN} olmalıdır`;
  else if (edit?.syncLevel > SYNC_LEVEL_MAX) errors.syncLevel = `Maksimum ${SYNC_LEVEL_MAX} olmalıdır`;
  return errors;
};

// ─── Build form state from device ───
const buildFormState = (device) => {
  const productEdits = {};
  if (device?.productInfo?.productInfo) {
    device.productInfo.productInfo.forEach((p) => {
      productEdits[p.productName] = {
        quotaWarning: p.quotaWarning ?? 0,
        quotaMax: p.quotaMax ?? 0,
        syncLevel: p.syncLevel ?? 0,
        quotaDistribution: p.quotaDistribution ?? false,
      };
    });
  }
  return {
    note: device?.note || "",
    productEdits,
  };
};

const InfoTab = ({
  selectedDevice,
  setDevice,
  setDevices,
  setAlerts,
  onShowDeveloperPassword,
  onHideDeveloperPassword,
  developerPassword,
  devPwdLoading,
  onRequestDelete,
  onRequestRefreshCreds,
  refreshCredsLoading,
}) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();

  // ─── Rol-farkındalıklı düzenleme yetkileri ──────────────────────────────
  // Bayi (`dealer`) yalnız cihaz notunu düzenler (`devices.editInfo`). Ürün
  // bilgisi (uyarı kotası, senkronizasyon, maksimum kota, dağıtım) bayide
  // salt okunur (#302). Versiyon/GSM kartları bayide hiç yok — anahtarlar
  // usePermission'da bayi için düşer. Depo tarafı granüler izinleri kullanır.
  const isDealer = auth?.role === "dealer";

  // ─── Kart bazlı görüntüleme yetkileri ───────────────────────────────────
  // `devices.info` yalnızca sekmeyi açar; sekme içindeki her kart kendi
  // anahtarıyla yönetilir. Aynı anahtarlar hem depo hem bayi kataloğunda var.
  const canViewFirmCard = hasPermission("devices.infoFirm");
  const canViewVersionCard = hasPermission("devices.infoVersion");
  const canViewGsmCard = hasPermission("devices.infoGsm");
  const canViewNoteCard = hasPermission("devices.infoNote");
  const canViewProductCards = hasPermission("devices.infoProducts");

  // Düzenleme yetkisi görüntüleme yetkisini KAPSAMAZ: kartı göremeyen
  // düzenleyemez de (aksi halde gizli kart için kaydet barı açılırdı).
  const canEditNote =
    canViewNoteCard &&
    (isDealer
      ? hasPermission("devices.editInfo")
      : hasPermission("devices.editNote"));
  const canEditMax =
    canViewProductCards && !isDealer && hasPermission("devices.editQuotaSettings");
  const canEditWarningSync =
    canViewProductCards && !isDealer && hasPermission("devices.editQuotaSettings");
  const canEditDistribution =
    canViewProductCards &&
    !isDealer &&
    hasPermission("devices.editQuotaDistribution");
  const canRemoveProduct =
    canViewProductCards && !isDealer && hasPermission("devices.editQuotaSettings");
  const canEditProduct = canEditMax || canEditWarningSync || canEditDistribution;
  // Çalışan ödeme makinesi (#391) — yalnız müşteri firmasındaki cihazda, izinle
  const canEditEmployeeOnly =
    !isDealer &&
    selectedDevice?.firm?.firmType === "customer" &&
    hasPermission("devices.employeeOnlyPayment");
  // Son 7 gün ret sayısı (#410) — yalnız kart görünür ve retleri görme izni varsa
  const canViewRejections =
    canEditEmployeeOnly && hasPermission("devices.paymentRejections");

  const [form, setForm] = useState(() => buildFormState(selectedDevice));
  const [editingNote, setEditingNote] = useState(false);
  const [devPwdCopied, setDevPwdCopied] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // productName string or null
  const [saving, setSaving] = useState(false);

  const {
    updateDevice,
    updateDeviceIsLoading,
    updateDeviceIsSuccess,
    updateDeviceIsError,
    updateDeviceError,
  } = useUpdateDeviceData();

  const {
    updateDeviceSync,
    updateDeviceSyncIsLoading,
    updateDeviceSyncIsSuccess,
    updateDeviceSyncIsError,
    updateDeviceSyncError,
  } = useUpdateDeviceSyncData();

  const [
    removeDeviceProduct,
    {
      isLoading: removeProductIsLoading,
      isSuccess: removeProductIsSuccess,
      isError: removeProductIsError,
      error: removeProductError,
    },
  ] = useRemoveDeviceProductMutation();

  // Çalışan ödeme makinesi anahtarı — not kaydının durumuyla karışmasın
  // diye ayrı mutation örneği (#391)
  const [updateEmployeeOnly, { isLoading: employeeOnlySaving }] =
    useUpdateDeviceInformationMutation();

  const { data: rejectionsData } = useGetPaymentRejectionsQuery(
    { token: auth.accessToken, id: selectedDevice?._id, limit: 1 },
    { skip: !canViewRejections || !selectedDevice?._id }
  );

  // Kilit + kaynak anahtarları (#391/#410) aynı uçtan, tek alanla güncellenir
  const EMPLOYEE_ONLY_LABELS = {
    employeeOnlyPayment: "Çalışan ödeme makinesi",
    employeeOnlyAllowWallet: "Kişisel cüzdanla ödeme",
    employeeOnlyAllowOkkalira: "Okkalira kullanımı",
  };
  const handleEmployeeOnlyChange = async (field, enabled) => {
    const alertId = Date.now();
    try {
      await updateEmployeeOnly({
        token: auth.accessToken,
        id: selectedDevice._id,
        [field]: enabled,
      }).unwrap();
      const updatedDevice = { ...selectedDevice, [field]: enabled };
      setDevices((prev) =>
        prev.map((d) => (d._id === selectedDevice._id ? updatedDevice : d))
      );
      if (typeof setDevice === "function") setDevice(updatedDevice);
      setAlerts((prev) => [
        ...prev,
        {
          id: alertId,
          message: `${EMPLOYEE_ONLY_LABELS[field]} ${enabled ? "açıldı" : "kapatıldı"}`,
          severity: "success",
        },
      ]);
    } catch (err) {
      setAlerts((prev) => [
        ...prev,
        {
          id: alertId,
          message: err?.data?.message || `${EMPLOYEE_ONLY_LABELS[field]} güncellenemedi`,
          severity: "error",
        },
      ]);
    }
    setTimeout(() => setAlerts((prev) => prev.filter((a) => a.id !== alertId)), 3000);
  };

  // Bayi cihaz not düzenleme (devices.editInfo)
  const [dealerUpdateDeviceInfo, { isLoading: dealerNoteLoading }] =
    useDealerUpdateDeviceInfoMutation();

  // Sync form state when selectedDevice changes
  useEffect(() => {
    if (selectedDevice) {
      setForm(buildFormState(selectedDevice));
      setEditingNote(false);
      setEditingProduct(null);
    }
  }, [selectedDevice]);

  // Original form for comparison
  const originalForm = useMemo(() => buildFormState(selectedDevice), [selectedDevice]);

  // Detect changes
  const noteChanged = useMemo(() => form.note !== originalForm.note, [form.note, originalForm.note]);

  const changedProducts = useMemo(() => {
    const changed = [];
    Object.keys(form.productEdits).forEach((pName) => {
      const curr = form.productEdits[pName];
      const orig = originalForm.productEdits[pName];
      if (!orig) return;
      if (
        curr.quotaWarning !== orig.quotaWarning ||
        curr.quotaMax !== orig.quotaMax ||
        curr.syncLevel !== orig.syncLevel ||
        curr.quotaDistribution !== orig.quotaDistribution
      ) {
        changed.push(pName);
      }
    });
    return changed;
  }, [form.productEdits, originalForm.productEdits]);

  const hasChanges = noteChanged || changedProducts.length > 0;

  const hasInvalidChangedProduct = useMemo(
    () =>
      changedProducts.some(
        (pName) => Object.keys(getProductFieldErrors(form.productEdits[pName])).length > 0
      ),
    [changedProducts, form.productEdits]
  );

  const isProductDirty = useCallback((productName) => {
    return changedProducts.includes(productName);
  }, [changedProducts]);

  // Update form helpers
  const updateNote = (val) => setForm((prev) => ({ ...prev, note: val }));
  const updateProductField = (productName, field, value) => {
    setForm((prev) => ({
      ...prev,
      productEdits: {
        ...prev.productEdits,
        [productName]: {
          ...prev.productEdits[productName],
          [field]: value,
        },
      },
    }));
  };

  const handleReset = () => {
    setForm(buildFormState(selectedDevice));
    setEditingNote(false);
    setEditingProduct(null);
  };

  const handleRemoveProduct = async (productName, productIndex) => {
    if (!selectedDevice || !hasPermission("devices.editQuotaSettings")) return;
    const list = selectedDevice?.productInfo?.productInfo || [];
    if (list.length <= 1) return;
    if (typeof productIndex !== "number" || productIndex < 0 || productIndex >= list.length) return;
    const confirmed = window.confirm(
      `"${productName}" ürününü bu cihazdan kaldırmak istediğinize emin misiniz?`
    );
    if (!confirmed) return;
    try {
      await removeDeviceProduct({
        id: selectedDevice._id,
        productName,
        productIndex,
        token: auth.accessToken,
      }).unwrap();
      const successAlertId = Date.now();
      setAlerts((prev) => [
        ...prev,
        { id: successAlertId, message: "Ürün cihazdan kaldırıldı", severity: "success" },
      ]);
      setTimeout(() => {
        setAlerts((prev) => prev.filter((a) => a.id !== successAlertId));
      }, 3000);
      const updatedProductInfo = list.filter((_, i) => i !== productIndex);
      const updatedDevice = {
        ...selectedDevice,
        productInfo: {
          ...selectedDevice.productInfo,
          productInfo: updatedProductInfo,
        },
      };
      setDevices((prev) =>
        prev.map((d) => (d._id === selectedDevice._id ? updatedDevice : d))
      );
      if (typeof setDevice === "function") setDevice(updatedDevice);
      setForm(buildFormState(updatedDevice));
      setEditingProduct(null);
    } catch (err) {
      const errorAlertId = Date.now();
      setAlerts((prev) => [
        ...prev,
        {
          id: errorAlertId,
          message: err?.data?.message || "Ürün kaldırılırken bir hata oluştu",
          severity: "error",
        },
      ]);
      setTimeout(() => setAlerts((prev) => prev.filter((a) => a.id !== errorAlertId)), 3000);
    }
  };

  // Success/Error handlers for updateDevice
  useEffect(() => {
    if (updateDeviceIsSuccess && selectedDevice) {
      const newAlert = { id: Date.now(), message: "Not başarıyla güncellendi", severity: "success" };
      setAlerts((prev) => [...prev, newAlert]);
      setEditingNote(false);
      const updatedDevice = { ...selectedDevice, note: form.note };
      setDevices((prevDevices) =>
        prevDevices.map((d) => (d._id === selectedDevice._id ? updatedDevice : d))
      );
      if (typeof setDevice === "function") {
        setDevice(updatedDevice);
      }
      setTimeout(() => { setAlerts((prev) => prev.filter((a) => a.id !== newAlert.id)); }, 3000);
    }
    if (updateDeviceIsError) {
      const newAlert = { id: Date.now(), message: updateDeviceError?.data?.message || "Not güncellenirken bir hata oluştu", severity: "error" };
      setAlerts((prev) => [...prev, newAlert]);
      setTimeout(() => { setAlerts((prev) => prev.filter((a) => a.id !== newAlert.id)); }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDeviceIsSuccess, updateDeviceIsError]);

  // Success/Error handlers for updateDeviceSync
  useEffect(() => {
    if (updateDeviceSyncIsSuccess && selectedDevice) {
      const newAlert = { id: Date.now(), message: "Ürün ayarları başarıyla güncellendi", severity: "success" };
      setAlerts((prev) => [...prev, newAlert]);
      setEditingProduct(null);
      const updatedDevice = {
        ...selectedDevice,
        productInfo: {
          ...selectedDevice.productInfo,
          productInfo: selectedDevice.productInfo.productInfo.map((product) => {
            const edit = form.productEdits[product.productName];
            if (edit && changedProducts.includes(product.productName)) {
              return {
                ...product,
                quotaWarning: Number(edit.quotaWarning),
                quotaMax: Number(edit.quotaMax),
                syncLevel: Number(edit.syncLevel),
                quotaDistribution: edit.quotaDistribution,
              };
            }
            return product;
          }),
        },
      };
      setDevices((prevDevices) =>
        prevDevices.map((d) => (d._id === selectedDevice._id ? updatedDevice : d))
      );
      if (typeof setDevice === "function") {
        setDevice(updatedDevice);
      }
      setTimeout(() => { setAlerts((prev) => prev.filter((a) => a.id !== newAlert.id)); }, 3000);
    }
    if (updateDeviceSyncIsError) {
      const newAlert = { id: Date.now(), message: updateDeviceSyncError?.data?.message || "Ürün güncellenirken bir hata oluştu", severity: "error" };
      setAlerts((prev) => [...prev, newAlert]);
      setTimeout(() => { setAlerts((prev) => prev.filter((a) => a.id !== newAlert.id)); }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDeviceSyncIsSuccess, updateDeviceSyncIsError]);

  // Kaydetme sonrası cihazın yerel kopyasını güncelleyip alan (bayi akışında
  // effect yerine inline kullanılır; depo akışı effect'lerle yönetilir).
  const applyLocalNote = () => {
    const updatedDevice = { ...selectedDevice, note: form.note };
    setDevices((prev) =>
      prev.map((d) => (d._id === selectedDevice._id ? updatedDevice : d))
    );
    if (typeof setDevice === "function") setDevice(updatedDevice);
    return updatedDevice;
  };

  const pushAlert = (message, severity) => {
    const id = Date.now() + Math.random();
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => setAlerts((prev) => prev.filter((a) => a.id !== id)), 3000);
  };

  // Bayi kaydetme akışı — yalnız not (#302); dealer endpoint'ine unwrap ile
  // gider, sonucu inline işler (depo effect'leri bayi mutation'larını dinlemez).
  const handleDealerSave = async () => {
    if (noteChanged) {
      try {
        await dealerUpdateDeviceInfo({
          id: selectedDevice._id,
          note: form.note,
          token: auth.accessToken,
        }).unwrap();
        applyLocalNote();
        setEditingNote(false);
        pushAlert("Not başarıyla güncellendi", "success");
      } catch (err) {
        pushAlert(
          err?.data?.message || "Not güncellenirken bir hata oluştu",
          "error"
        );
      }
    }
  };

  const handleSave = async () => {
    if (!selectedDevice || !hasChanges) return;
    if (hasInvalidChangedProduct) {
      pushAlert(
        `Uyarı kotası ${QUOTA_WARNING_MIN}-${QUOTA_WARNING_MAX}, senk. seviyesi ${SYNC_LEVEL_MIN}-${SYNC_LEVEL_MAX} aralığında olmalıdır`,
        "error"
      );
      return;
    }
    setSaving(true);

    try {
      if (isDealer) {
        await handleDealerSave();
        setSaving(false);
        return;
      }

      // Save note if changed
      if (noteChanged) {
        updateDevice({
          note: form.note,
          customName: selectedDevice.customName,
          token: auth.accessToken,
          id: selectedDevice._id,
        });
      }

      // Save each changed product
      for (const pName of changedProducts) {
        const edit = form.productEdits[pName];
        updateDeviceSync({
          productName: pName,
          quotaWarning: Number(edit.quotaWarning),
          quotaMax: Number(edit.quotaMax),
          syncLevel: Number(edit.syncLevel),
          quotaDistribution: edit.quotaDistribution,
          token: auth.accessToken,
          id: selectedDevice._id,
        });
      }
    } catch (e) { /* handled by effects */ }

    setSaving(false);
  };

  // Developer parolası kopyala (inline box — göz ile reveal sonrası)
  const handleCopyDevPwd = async () => {
    if (!developerPassword) return;
    try {
      await navigator.clipboard.writeText(developerPassword);
      setDevPwdCopied(true);
      setTimeout(() => setDevPwdCopied(false), 1800);
    } catch (_) {
      /* sessiz geç */
    }
  };

  // ---- Render helpers ----

  const InfoRow = ({ label, value, valueClassName = "", badge = false, badgeColor = "" }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0 gap-3">
      <span className="text-xs font-medium text-gray-500 shrink-0">{label}</span>
      {badge ? (
        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full shrink-0 ${badgeColor}`}>
          {value || "—"}
        </span>
      ) : (
        <span className={`text-[13px] font-semibold text-gray-900 text-right truncate ${valueClassName}`}>
          {value || <span className="text-gray-300">—</span>}
        </span>
      )}
    </div>
  );

  const renderDeviceDetails = () => {
    if (!selectedDevice) {
      return (
        <div className="text-center text-gray-400 p-8 text-sm">
          Lütfen bilgilerini görüntülemek istediğiniz cihazı seçin
        </div>
      );
    }

    const statusBadge = {
      Firma: "bg-blue-100 text-blue-800",
      Depo: "bg-amber-100 text-amber-800",
      Servis: "bg-purple-100 text-purple-800",
    }[selectedDevice.deviceStatusName] || "bg-gray-200 text-gray-700";

    const firm = selectedDevice.firm;
    // "Firmaya git" linki, kullanıcının firma bilgi görme izni varsa gösterilir.
    // Depo: firms.info. Bayi: firma tipine göre customers.info / dealers.info.
    const isDealerUser = auth?.role === "dealer";
    const firmInfoPermission = isDealerUser
      ? firm?.firmType === "customer"
        ? "customers.info"
        : "dealers.info"
      : "firms.info";
    const canViewFirmInfo = !!firm?._id && hasPermission(firmInfoPermission);
    // Firma tipi renkleri tek kaynaktan (#215)
    const { label: firmTypeLabel, chip: firmTypeBadgeColor } = getFirmTypeStyle(firm);
    const activeBadgeColor = firm?.isActive ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700";

    const cards = [
      canViewFirmCard && {
        icon: <FaBuilding className="w-4 h-4" />,
        iconBg: "bg-blue-500",
        title: "Firma Bilgileri",
        clickable: canViewFirmInfo,
        linkTo: selectedDevice.firm?._id ? `/Anasayfa/Firma/${selectedDevice.firm._id}` : null,
        linkState: { firm: selectedDevice.firm, fromPage: "Cihaz" },
        customFirmaLayout: true,
        rows: [
          { label: "Bayser No", value: selectedDevice.firm?.bayserNo },
        ],
      },
      // VERSİYONLAR ızgarada DEĞİL — aşağıda tam genişlikte ayrı satır
      // (DeviceVersionsCard, sözleşme device-info-cards.md).
      canViewGsmCard && {
        icon: <FaNetworkWired className="w-4 h-4" />,
        iconBg: "bg-emerald-500",
        title: "GSM Bilgileri",
        rows: [
          { label: "IMEI", value: selectedDevice.deviceInfo?.imei },
          { label: "GSM No", value: selectedDevice.deviceInfo?.gsmNumber },
          { label: "Operatör", value: selectedDevice.deviceInfo?.carrier },
        ],
      },
    ].filter(Boolean);

    return (
      <div className="space-y-4">
        {/* Üst 3 kart + Not kartı */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 px-3 sm:px-0">
          {cards.map((card, i) => (
            <div
              key={i}
              className={`relative rounded-xl bg-white border border-gray-200 shadow-sm sm:hover:shadow-md transition-all overflow-hidden ${
                card.clickable ? "cursor-pointer hover:border-blue-300 group/card" : ""
              }`}
            >
              {card.clickable && card.linkTo && (
                <AppLink
                  to={card.linkTo}
                  state={card.linkState}
                  className="absolute inset-0 z-0"
                  aria-label="Firma bilgilerine git"
                />
              )}
              {/* Kart başlığı */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className={`${card.iconBg} text-white p-1.5 rounded-lg shadow-sm`}>
                    {card.icon}
                  </div>
                  <span className="text-sm font-SemiBold text-gray-800">{card.title}</span>
                </div>
                {card.clickable && (
                  <FaExternalLinkAlt className="w-3 h-3 text-gray-300 group-hover/card:text-blue-500 transition-colors" />
                )}
              </div>
              {/* İçerik */}
              <div className="px-4 py-1">
                {card.customFirmaLayout ? (
                  <>
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 gap-3 flex-nowrap">
                      <span className="text-xs font-medium text-gray-500 shrink-0">Firma Adı</span>
                      <span className="text-[13px] font-semibold text-gray-900 text-right truncate">{selectedDevice.firm?.firmName || "—"}</span>
                    </div>
                    {card.rows.map((row, j) => (
                      <InfoRow key={j} {...row} />
                    ))}
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0 gap-3 flex-nowrap">
                      <span className="text-xs font-medium text-gray-500 shrink-0">Firma Durumu</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${firmTypeBadgeColor}`}>{firmTypeLabel}</span>
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${activeBadgeColor}`}>{selectedDevice.firm?.isActive ? "Aktif" : "Pasif"}</span>
                      </div>
                    </div>
                    {selectedDevice.employeeOnlyPayment && (
                      <div className="flex items-center justify-between py-2.5 gap-3 flex-nowrap">
                        <span className="text-xs font-medium text-gray-500 shrink-0">Ödeme</span>
                        <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Yalnız çalışanlar</span>
                      </div>
                    )}
                  </>
                ) : (
                  card.rows.map((row, j) => (
                    <InfoRow key={j} {...row} />
                  ))
                )}
              </div>
            </div>
          ))}

          {/* Not Bilgileri — devices.infoNote */}
          {canViewNoteCard && (
          <div className={`rounded-xl bg-white border shadow-sm sm:hover:shadow-md transition-shadow overflow-hidden ${noteChanged ? "border-blue-300" : "border-gray-200"}`}>
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="bg-amber-500 text-white p-1.5 rounded-lg shadow-sm">
                  <FaStickyNote className="w-4 h-4" />
                </div>
                <span className="text-sm font-SemiBold text-gray-800">Notlar</span>
                {noteChanged && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
              </div>
              {!editingNote ? (
                canEditNote && <button
                  onClick={() => setEditingNote(true)}
                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  title="Düzenle"
                >
                  <FaPencilAlt className="w-3 h-3" />
                </button>
              ) : (
                <button
                  onClick={() => setEditingNote(false)}
                  className="px-2 py-0.5 text-[11px] font-medium text-gray-500 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Kapat
                </button>
              )}
            </div>
            <div className="px-4 pb-3">
              {editingNote ? (
                <div className="pt-2">
                  <textarea
                    value={form.note}
                    onChange={(e) => updateNote(e.target.value)}
                    rows={4}
                    placeholder="Cihaz için not ekleyin..."
                    className="w-full text-sm text-gray-900 bg-white border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  />
                </div>
              ) : (
                <div className="pt-2 min-h-[44px] bg-transparent">
                  {form.note ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {form.note}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Not yok</p>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {canEditEmployeeOnly && (
            <EmployeeOnlyPaymentCard
              enabled={!!selectedDevice.employeeOnlyPayment}
              allowWallet={selectedDevice.employeeOnlyAllowWallet !== false}
              allowOkkalira={selectedDevice.employeeOnlyAllowOkkalira !== false}
              saving={employeeOnlySaving}
              onToggle={(enabled) => handleEmployeeOnlyChange("employeeOnlyPayment", enabled)}
              onSourceToggle={handleEmployeeOnlyChange}
              rejections7d={rejectionsData?.data?.last7Days}
            />
          )}

          {/* Cihaz Bilgileri — yönetici işlemleri kutusu. Diğer kartlarla aynı
              tasarım/boyut, grid'in son hücresi. Parola satırı
              devices.developerPassword iznine, Sil/Creds satırları admin'e
              bağlı (parent verir); en az bir satır varsa kutu görünür.
              Developer parolası göz ikonuyla inline reveal edilir. */}
          {(onShowDeveloperPassword ||
            onRequestDelete ||
            onRequestRefreshCreds) && (
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm sm:hover:shadow-md transition-shadow overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="bg-slate-700 text-white p-1.5 rounded-lg shadow-sm">
                    <FaKey className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-SemiBold text-gray-800">Cihaz Bilgileri</span>
                </div>
              </div>
              <div className="px-4 py-1">
                {onShowDeveloperPassword && (
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0 gap-3">
                    <span className="text-xs font-medium text-gray-500 shrink-0">Developer Parolası</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="w-28 text-right text-[13px] font-mono font-semibold text-gray-900 truncate">
                        {developerPassword || "••••••"}
                      </span>
                      <button
                        type="button"
                        onClick={handleCopyDevPwd}
                        title="Kopyala"
                        disabled={!developerPassword}
                        className={`p-1 shrink-0 ${
                          developerPassword
                            ? "text-gray-400 hover:text-indigo-600"
                            : "invisible pointer-events-none"
                        }`}
                      >
                        {devPwdCopied ? (
                          <FaCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <FaCopy className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          developerPassword
                            ? onHideDeveloperPassword?.()
                            : onShowDeveloperPassword?.()
                        }
                        disabled={devPwdLoading}
                        title={developerPassword ? "Gizle" : "Göster"}
                        className="p-1 text-gray-400 hover:text-indigo-600 shrink-0 disabled:opacity-50"
                      >
                        {devPwdLoading ? (
                          <span className="inline-block w-3.5 h-3.5 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
                        ) : developerPassword ? (
                          <FaEyeSlash className="w-3.5 h-3.5" />
                        ) : (
                          <FaEye className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {onRequestRefreshCreds && (
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0 gap-3">
                    <span className="text-xs font-medium text-gray-500 shrink-0">Kimlik Bilgileri</span>
                    <button
                      type="button"
                      onClick={onRequestRefreshCreds}
                      disabled={refreshCredsLoading}
                      title="Cihazın kimlik bilgilerini yenile ve cihaza gönder"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 hover:shadow-md active:bg-indigo-800 transition-all shrink-0 disabled:opacity-50"
                    >
                      {refreshCredsLoading ? (
                        <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <FaSyncAlt className="w-3 h-3" />
                      )}{" "}
                      Creds Yenile
                    </button>
                  </div>
                )}
                {onRequestDelete && (
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0 gap-3">
                    <span className="text-xs font-medium text-gray-500 shrink-0">Cihazı Sil</span>
                    <button
                      type="button"
                      onClick={onRequestDelete}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white shadow-sm hover:bg-red-700 hover:shadow-md active:bg-red-800 transition-all shrink-0"
                    >
                      <FaTrash className="w-3 h-3" /> Sil
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* VERSİYONLAR — devices.infoVersion; kart listesi tam genişlikte */}
        {canViewVersionCard && (
          <div className="px-3 sm:px-0">
            <DeviceVersionsCard deviceInfo={selectedDevice.deviceInfo} />
          </div>
        )}
      </div>
    );
  };

  const renderProductInfo = (productInfo) => {
    if (!productInfo || !productInfo.productInfo || productInfo.productInfo.length === 0) {
      return (
        <div className="text-center text-gray-300 py-6 text-sm">
          Ürün bilgisi bulunmuyor
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 px-3 sm:px-0">
        {productInfo.productInfo.map((product, index) => {
          const isEditing = editingProduct === product.productName;
          const dirty = isProductDirty(product.productName);
          const edit = form.productEdits[product.productName] || {};
          const fieldErrors = isEditing ? getProductFieldErrors(edit) : {};
          const quotaPercent = product.quotaMax > 0 ? Math.min(100, Math.round((product.quota / product.quotaMax) * 100)) : 0;
          const quotaBoxColor = quotaPercent > 80 ? "bg-red-50 border-red-200 text-red-700" : quotaPercent > 50 ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700";

          return (
            <div key={index} className={`rounded-xl bg-white border shadow-sm sm:hover:shadow-md transition-shadow overflow-hidden ${dirty ? "border-blue-300" : "border-gray-200"}`}>
              {/* Ürün Header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="bg-indigo-500 text-white p-1.5 rounded-lg shadow-sm">
                    <FaCoffee className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <span className="text-sm font-SemiBold text-gray-900 block">{product.productName}</span>
                    {product.connectionStatus === 1 && (
                      <span className="text-xs text-amber-600 font-semibold animate-pulse">Senkronize bekliyor</span>
                    )}
                    {/* CEVRIMDISI IADE MAHSUBU BEKLIYOR.
                        Cihaz cevrimdisiyken kotasi firmaya iade edildi; iade
                        edilen miktar DB'deki bayat degerdi. Cihaz baglanip
                        gercek kalanini bildirince aradaki fark firmadan
                        dusulecek. Bu rozet o bekleyen mahsubu gosterir. */}
                    {Number(product.pendingReturnedQuota) > 0 && (
                      <span className="block text-xs text-rose-600 font-semibold animate-pulse">
                        Mahsup bekliyor: {Number(product.pendingReturnedQuota).toLocaleString("tr-TR")}
                      </span>
                    )}
                  </div>
                  {dirty && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                </div>
                <div className="flex items-center gap-0.5">
                  {canEditProduct && (
                    <button
                      onClick={() => setEditingProduct(isEditing ? null : product.productName)}
                      className={`p-1.5 rounded-lg transition-colors ${isEditing ? "text-indigo-600 bg-indigo-50" : "text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"}`}
                      title={isEditing ? "Kapat" : "Düzenle"}
                    >
                      <FaPencilAlt className="w-3 h-3" />
                    </button>
                  )}
                  {canRemoveProduct && productInfo.productInfo.length > 1 && (
                    <button
                      onClick={() => handleRemoveProduct(product.productName, index)}
                      disabled={removeProductIsLoading}
                      className="p-1.5 rounded-lg transition-colors text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                      title="Ürünü cihazdan kaldır"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="p-4 space-y-3">
                  {/* Ürün Adı (read-only) */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Ürün Adı</label>
                    <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">{product.productName}</div>
                  </div>
                  {(canEditMax || canEditWarningSync) && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        {canEditMax && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Maximum Kota</label>
                            <input
                              type="number"
                              value={edit.quotaMax ?? 0}
                              onChange={(e) => updateProductField(product.productName, "quotaMax", Number(e.target.value) || 0)}
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                            />
                          </div>
                        )}
                        {canEditWarningSync && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Uyarı Kotası</label>
                            <input
                              type="number"
                              min={QUOTA_WARNING_MIN}
                              max={QUOTA_WARNING_MAX}
                              value={edit.quotaWarning ?? 0}
                              onChange={(e) => updateProductField(product.productName, "quotaWarning", Number(e.target.value) || 0)}
                              className={`w-full px-3 py-2 bg-white border rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent transition-all ${fieldErrors.quotaWarning ? "border-red-300 focus:ring-red-400" : "border-gray-200 focus:ring-blue-400"}`}
                            />
                            {fieldErrors.quotaWarning && (
                              <p className="text-red-500 text-[11px] mt-0.5">{fieldErrors.quotaWarning}</p>
                            )}
                          </div>
                        )}
                      </div>
                      {canEditWarningSync && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Senkronizasyon</label>
                          <input
                            type="number"
                            min={SYNC_LEVEL_MIN}
                            max={SYNC_LEVEL_MAX}
                            value={edit.syncLevel ?? 0}
                            onChange={(e) => updateProductField(product.productName, "syncLevel", Number(e.target.value) || 0)}
                            className={`w-full px-3 py-2 bg-white border rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent transition-all ${fieldErrors.syncLevel ? "border-red-300 focus:ring-red-400" : "border-gray-200 focus:ring-blue-400"}`}
                          />
                          {fieldErrors.syncLevel && (
                            <p className="text-red-500 text-[11px] mt-0.5">{fieldErrors.syncLevel}</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {/* Kota Dağıtımı Toggle */}
                  {canEditDistribution && (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-xs font-semibold text-gray-600">Kota Dağıtımı Aktif</span>
                      <button
                        onClick={() => updateProductField(product.productName, "quotaDistribution", !edit.quotaDistribution)}
                        className="p-0.5 transition-colors"
                      >
                        {edit.quotaDistribution ? (
                          <FaToggleOn className="w-7 h-7 text-emerald-500 hover:text-emerald-600 transition-colors" />
                        ) : (
                          <FaToggleOff className="w-7 h-7 text-gray-300 hover:text-gray-400 transition-colors" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-3 space-y-3">
                  {/* Kota — 3 stat box üstünde büyük badge box (kullanım
                      seviyesine göre renkli; maksimum sınır gösterilmez). */}
                  <div className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-3 ${quotaBoxColor}`}>
                    <span className="text-xs font-semibold uppercase tracking-wide opacity-80">Kota</span>
                    <span className="text-3xl font-extrabold leading-none">
                      {product.quota?.toLocaleString("tr-TR")}
                    </span>
                  </div>

                  {/* Stat grid */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-2.5 text-center">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Uyarı</p>
                      <p className="text-sm font-bold text-gray-900">{product.quotaWarning?.toLocaleString("tr-TR")}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-2.5 text-center">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Sayaç</p>
                      <p className="text-sm font-bold text-gray-900">{product.counter?.toLocaleString("tr-TR")}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-2.5 text-center">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Senk.</p>
                      <p className="text-sm font-bold text-gray-900">{product.syncLevel}</p>
                    </div>
                  </div>

                  {/* Kota Dağıtımı */}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs font-medium text-gray-500">Kota Dağıtımı</span>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${
                      product.quotaDistribution ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${product.quotaDistribution ? "bg-emerald-500" : "bg-red-500"}`} />
                      {product.quotaDistribution ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full space-y-3 sm:space-y-4 p-3 sm:p-4">
          {renderDeviceDetails()}
          {canViewProductCards &&
            selectedDevice &&
            selectedDevice.productInfo &&
            renderProductInfo(selectedDevice.productInfo)}
          {/* Manuel kota talebi (QR, makine 3.9.4+) — ürünlerin altında; talep yoksa çizilmez (#115).
              Yalnız merkez — bayi manuel kota yükleyemez (#178). */}
          {!isDealer && hasPermission("devices.quota") && selectedDevice?._id && (
            <StorageManualQuotaRequestSection deviceId={selectedDevice._id} />
          )}
        </div>
      </div>

      {/* Sticky Save Bar */}
      {(canEditNote || canEditProduct) && hasChanges && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-2.5 sm:px-5 sm:py-3">
          <div className="flex items-center justify-between">
            <p className="text-xs sm:text-sm text-gray-500">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Kaydedilmemiş değişiklikler var</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={handleReset} disabled={saving || updateDeviceIsLoading || updateDeviceSyncIsLoading || dealerNoteLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50">
                <FaUndo className="w-3 h-3" /> <span>Geri Al</span>
              </button>
              <button onClick={handleSave} disabled={saving || updateDeviceIsLoading || updateDeviceSyncIsLoading || dealerNoteLoading || hasInvalidChangedProduct}
                className="flex items-center gap-1.5 px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl hover:from-indigo-700 hover:to-blue-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                {(saving || updateDeviceIsLoading || updateDeviceSyncIsLoading || dealerNoteLoading) ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaSave className="w-3.5 h-3.5" />
                )}
                <span>{(saving || updateDeviceIsLoading || updateDeviceSyncIsLoading || dealerNoteLoading) ? "Kaydediliyor..." : "Kaydet"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoTab;
